import React, { useContext, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Container } from "@mui/material";
import {
  Box,
  Paper,
  Spoiler,
  Tabs,
  Text,
  Accordion,
  Anchor,
  Badge,
  Group,
  Divider,
  Stack,
  Button,
} from "@mantine/core";
import {
  IconChevronRight,
  IconClock,
  IconNotebook,
  IconUserCircle,
  IconPlayerPlay,
  IconFileText,
  IconDownload,
  IconBook,
  IconMessageCircle,
  IconFileAnalytics,
  IconVideo,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import MegaLoader from "../../Components/MegaLoader";
import { Colors, HttpErrorCodes } from "../../Lib/data";
import { Endpoints } from "../../Lib/endpoints";
import { DefaultErrorNotification } from "../../Lib/methods";
import { NonPaginatedResponse, PaginatedResponse } from "../../Lib/responses";
import {
  CourseProps,
  HttpErrorCodesProps,
  LessonProps,
  TeacherProfileProps,
} from "../../Lib/types";
import { usePerformRequest } from "../../Lib/usePerformRequest";
import { AppContext } from "../Dashboard";
import "./styles.scss";
import dayjs from "dayjs";

const SingleCourse: React.FC = () => {
  const { t } = useTranslation();
  const context = useContext(AppContext);
  const { courseID } = useParams();
  const navigate = useNavigate();
  const {
    data: course,
    isLoading: isLoadingCourse,
    reloadData: reloadCourse,
    response: courseAxiosResponse,
  } = usePerformRequest<NonPaginatedResponse<CourseProps & { isAuth: boolean }>>({
    route: Endpoints.GetSingleCourseDetailsAuth(courseID as string).route,
    method: Endpoints.GetSingleCourseDetailsAuth(courseID as string).method,
  });

  const {
    data: teacher,
    isLoading: isLoadingTeacher,
    reloadData: reloadTeacher,
    response: teacherAxiosResponse,
  } = usePerformRequest<NonPaginatedResponse<TeacherProfileProps>>({
    route: Endpoints.GetSingleTeacherProfile(course?.data?.teacherID as string).route,
    method: Endpoints.GetSingleTeacherProfile(course?.data?.teacherID as string).method,
  });

  const {
    data: lessons,
    isLoading: isLoadingLessons,
    reloadData: reloadLessons,
    response: lessonsAxiosResponse,
  } = usePerformRequest<PaginatedResponse<LessonProps[]>>({
    route: Endpoints.GetLessonsUnderCourse(courseID as string, 1, 30).route,
    method: Endpoints.GetLessonsUnderCourse(courseID as string, 1, 30).method,
  });

  useEffect(() => {
    if (courseAxiosResponse) {
      if (HttpErrorCodes.includes(courseAxiosResponse.status as HttpErrorCodesProps)) {
        DefaultErrorNotification("Could not get course details!");
        navigate("/d/courses");
      }
    }
  }, [isLoadingCourse, courseAxiosResponse]);

  return (
    <Container maxWidth="lg">
      <div className="single-course-container">
        {isLoadingCourse ? (
          <MegaLoader />
        ) : (
          <>
            <div className="course-header">
              <div className="course-thumbnail-section">
                <div
                  className="course-thumbnail"
                  style={{
                    backgroundImage:
                      course?.data.thumbnail && course?.data.thumbnail !== ""
                        ? `url(${course?.data.thumbnail})`
                        : course?.data.images && course?.data.images.length > 0
                        ? `url(${course?.data.images[0]})`
                        : "none",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  <div className="thumbnail-overlay" />
                </div>
              </div>

              <div className="course-content-grid">
                <div className="course-main-info">
                  <div className="course-title-section">
                    <Text
                      fw={700}
                      fz={32}
                      style={{
                        background: "linear-gradient(45deg, #228be6, #4dabf7)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      {course?.data.name}
                    </Text>
                    <Badge
                      color={course?.data.isActive ? "green" : "red"}
                      variant="light"
                      size="lg"
                      radius="xl"
                      mt={8}
                    >
                      {course?.data.isActive ? t("common.active") : t("common.inactive")}
                    </Badge>
                  </div>

                  <div className="course-description">
                    <Spoiler
                      maxHeight={70}
                      showLabel={t("courses.showMore")}
                      hideLabel={t("courses.hide")}
                    >
                      <Text fz={15} lh={1.7} c="dimmed">
                        {course?.data.description}
                      </Text>
                    </Spoiler>
                  </div>

                  <Group gap="md" mt="lg">
                    {course?.data?.isAuth && (
                      <Button
                        variant="light"
                        color={Colors.PurplePrimary}
                        leftSection={<IconVideo size={16} />}
                        onClick={() => navigate(`/d/recordings/${courseID}`)}
                        radius="md"
                      >
                        {t("courses.viewRecordings")}
                      </Button>
                    )}
                  </Group>
                </div>

                <div className="course-side-info">
                  <Paper withBorder radius="md" p="md" className="course-meta-card">
                    <div className="meta-section">
                      <Text fw={600} fz="sm" c="dimmed" mb={8}>
                        {t("common.createdAt")}
                      </Text>
                      <Text fw={500} fz="lg">
                        {dayjs(course?.data.createdAt).format("MMMM DD, YYYY")}
                      </Text>
                    </div>
                    <Divider my="md" />
                    <div className="meta-section">
                      <Text fw={600} fz="sm" c="dimmed" mb={8}>
                        {/* {t("common.status")} */}
                      </Text>
                      <Badge
                        color={course?.data.isActive ? "green" : "red"}
                        variant="light"
                        size="lg"
                        radius="xl"
                        w="fit-content"
                      >
                        {course?.data.isActive ? t("common.active") : t("common.inactive")}
                      </Badge>
                    </div>
                  </Paper>

                  <Paper withBorder radius="md" p="md" className="teacher-card">
                    <Group gap="md" align="center">
                      <div className="teacher-avatar">
                        <IconUserCircle
                          size={48}
                          color={
                            course?.data?.teacherID && course?.data?.teacherID?.length > 0
                              ? Colors.BluePrimary
                              : Colors.GraySecondary
                          }
                        />
                      </div>
                      <div className="teacher-details">
                        {course?.data?.teacherID && course?.data?.teacherID?.length > 0 ? (
                          <>
                            <Text fz={18} fw={600} c={Colors.BluePrimary}>
                              {teacher?.data?.name}
                            </Text>
                            <Text fz="sm" c="dimmed" mt={2}>
                              {t("courses.instructor")}
                            </Text>
                          </>
                        ) : (
                          <>
                            <Text
                              fz={16}
                              fw={500}
                              c={Colors.OrangePrimary}
                              style={{
                                textDecoration: "underline",
                                textDecorationColor: Colors.OrangePrimary,
                                textDecorationThickness: 1,
                                textUnderlineOffset: 4,
                                textDecorationStyle: "dashed",
                              }}
                            >
                              {t("lessons.students")}
                            </Text>
                            <Text fz="sm" c="dimmed" mt={2}>
                              {t("lessons.students")}
                            </Text>
                          </>
                        )}
                      </div>
                    </Group>
                  </Paper>
                </div>
              </div>
            </div>

            <Divider my={32} color="gray.2" style={{ opacity: 0.5 }} />
            <Text fw={600} fz={21} c={Colors.PurplePrimary} mb={3}>
              {t("lessons.title")}
            </Text>
            <Box mt={10}>
              <Accordion variant="separated" radius="md">
                {lessons?.data?.map((lesson: LessonProps, index: number) => (
                  <Accordion.Item key={lesson.id} value={lesson.id}>
                    <Accordion.Control>
                      <div className="lesson-header">
                        <div className="lesson-number">
                          <div className="number-circle">
                            <Text c="white" fw={500} size="xs">
                              {index + 1}
                            </Text>
                          </div>
                        </div>
                        <div className="lesson-info">
                          <div className="lesson-title">
                            <IconPlayerPlay size={16} style={{ marginRight: 8, opacity: 0.7 }} />
                            <Text fw={500} size="md">
                              {lesson.title}
                            </Text>
                          </div>
                          <div className="lesson-duration">
                            <IconClock size={16} style={{ opacity: 0.5 }} />
                            <Text size="sm" ml={6} c="dimmed">
                              {/* {lesson.runTimes?.[0]
                                ? `${Math.round(lesson.runTimes[0] / 60)} ${t("lessons.minutes")}`
                                : t("lessons.durationNA")} */}
                              {dayjs(lesson.initialRunTime).format("ddd, DD MMM YYYY HH:mmA")}
                            </Text>
                          </div>
                        </div>
                      </div>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <div className="lesson-content">
                        <Text c="dimmed" size="sm" mb={16}>
                          {lesson.description}
                        </Text>
                        <div className="lesson-actions">
                          <Anchor
                            component="button"
                            onClick={() => navigate(`/d/lesson/${lesson.id}`)}
                            className="action-button primary"
                          >
                            <IconPlayerPlay size={14} />
                            <Text fw={500} size="sm">
                              {t("courses.joinLesson")}
                            </Text>
                          </Anchor>
                          <Anchor
                            component="button"
                            onClick={() => navigate(`/d/assessments/${lesson.id}`)}
                            className="action-button"
                          >
                            <IconFileText size={14} />
                            <Text fw={500} size="sm">
                              {t("courses.viewAssessments")}
                            </Text>
                          </Anchor>
                          <Anchor
                            component="button"
                            onClick={() => navigate(`/d/materials/${lesson.id}`)}
                            className="action-button"
                          >
                            <IconDownload size={14} />
                            <Text fw={500} size="sm">
                              {t("courses.downloadMaterials")}
                            </Text>
                          </Anchor>
                        </div>
                      </div>
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>

              {(!lessons?.data || lessons.data.length === 0) && (
                <Paper p={40} radius="md" withBorder className="empty-state">
                  <div className="empty-state-content">
                    <IconBook size={48} color={Colors.BluePrimary} style={{ opacity: 0.5 }} />
                    <Text fw={600} fz={20} mt={16} c={Colors.BluePrimary}>
                      {t("lessons.title")}
                    </Text>
                    <Text fz="sm" c="dimmed" ta="center" mt={8}>
                      {t("lessons.title")}
                    </Text>
                  </div>
                </Paper>
              )}
            </Box>
          </>
        )}
      </div>
      <div className="flex-col">
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
      </div>

      {/* Floating Action Bar */}
      <div className="floating-performance-bar">
        <Paper shadow="xl" radius="xl" p="md" className="performance-bar-inner">
          <Stack gap={10} align="center">
            <IconBook size={32} color={Colors.PurplePrimary} />
            <Text fw={600} fz={18} ta="center">
              {t("courses.viewPerformanceCallout", {
                course: course?.data?.name,
              })}
            </Text>
            <Link to={`/d/performance/${courseID}`}>
              <Anchor component="button" className="performance-bar-btn">
                <IconFileAnalytics size={20} style={{ marginRight: 8 }} />
                <Text fw={700} fz={16}>
                  {t("courses.viewPerformance")}
                </Text>
              </Anchor>
            </Link>
          </Stack>
        </Paper>
      </div>
    </Container>
  );
};

export default SingleCourse;
